import { Location } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Auth0TokenResponse } from 'qp-ui-interface';
import { Subject } from 'rxjs';
import { AuthService } from 'src/app/shared/services/auth/auth.service';
import { DatazoomService } from '../../../analytics/datazoom/service/datazoom.service';
import { EventName } from '../../../analytics/newrelic/enums/EventName';
import { NewrelicService } from '../../../analytics/newrelic/service/newrelic.service';
import { DataService } from '../../../data.service';
import { ConfigService } from '../../services/config/config.service';
import { ProfileService } from '../../services/profiles/profile.service';
import { UserStateService } from '../../services/user-state/user-state.service';
import { cleanRoutes, isLoggedIn } from '../../utils/LoginUtils';
import { saveAccessToken } from '../../utils/tokenUtils';
import { getLoginCode, getUrl } from '../../utils/ttsUtils';
import { LocalStorageService } from '../../services/local-storage/local-storage.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  @Input() mode: 'vertical' | 'horizontal' = 'vertical';
  window = window;
  interval = 0;
  private fromLinkAccount = false;
  private hasLogin = false;
  activationCode = '';
  qrLink = '';
  qrLinkComplete = '';
  private deviceCode = '';
  // TTS
  qrTTSCode = '';
  qrTTSLink = '';

  // Intervals
  private destroy$ = new Subject<void>();
  constructor(
    private readonly router: Router,
    private readonly location: Location,
    private readonly route: ActivatedRoute,
    private readonly profileService: ProfileService,
    private readonly userState: UserStateService,
    private readonly authService: AuthService,
    private readonly datazoomService: DatazoomService,
    private readonly newrelic: NewrelicService,
    private readonly dataService: DataService,
    private readonly configService: ConfigService,
    private readonly localStorage: LocalStorageService
  ) {
    this.getActivationCode();
  }

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.fromLinkAccount = params?.fromLinkAccount === 'true';
      if (this.fromLinkAccount) {
        if (!isLoggedIn(this.localStorage)) {
          this.triggerActivation();
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.dataService.closeCodeInterval();
    this.dataService.closeTokenInterval();
  }

  goBack(): void {
    this.location.back();
  }

  private getActivationCode() {
    if (this.hasLogin) return;
    this.authService.getDeviceCode().subscribe((response) => {
      this.activationCode = response.device_code;
      this.qrTTSCode = getLoginCode(response.device_code);
      this.qrLink =
        this.configService.applicationConfig.linkDeviceURL?.length > 0 && this.configService.applicationConfig.linkDeviceURL !== 'false'
          ? this.configService.applicationConfig.linkDeviceURL
          : response.verification_uri;
      this.qrTTSLink = getUrl(this.qrLink);
      this.qrLinkComplete = response.verification_uri_complete;
      this.activationCode = response.user_code;
      this.deviceCode = response.device_code;
      this.dataService.closeTokenInterval();
      this.triggerActivation(response.interval);
      if (response.expires_in) {
        this.dataService.activationCodeInterval = setTimeout(() => {
          this.getActivationCode();
        }, response.expires_in * 1000 + 1);
      }
    });
  }

  /**
   * Manage activation succesful
   */
  private getActivationToken(): void {
    // number of attemps then what?
    this.authService.pollForToken(this.deviceCode).subscribe(
      async (response: Auth0TokenResponse) => {
        saveAccessToken(response, this.localStorage);
        this.dataService.closeTokenInterval();
        this.dataService.closeCodeInterval();
        // set selected profile
        const profResponse = await this.profileService.getProfiles();
        const mainProfile = profResponse.profiles.find((p) => p.isp);
        this.userState.updateSelectedProfile(mainProfile);
        this.datazoomService.loginUser();
        this.hasLogin = true;
        cleanRoutes(this.localStorage);
        this.newrelic.sendNewrelicEvent(EventName.LOGIN);
        this.router.navigate(['/on-boarding/profiles']);
      },
      () => {}
    );
  }

  private async triggerActivation(interval: number = 0) {
    this.getActivationToken();
    this.dataService.activationTokenInterval = setInterval(() => {
      if (!this.hasLogin) {
        this.getActivationToken();
      }
    }, interval * 2000);
  }
}
